'use client';

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import type { TypeFilterTab } from '@/components/modules/ModuleHeader';

interface NavTypeFilterState {
  typeFilterTabs: TypeFilterTab[];
  activeTypeFilter: string | null;
  onTypeFilterChange: (id: string | null) => void;
}

interface NavTypeFilterContextValue extends NavTypeFilterState {
  setTypeFilters: (state: NavTypeFilterState) => void;
}

const DefaultValue: NavTypeFilterContextValue = {
  typeFilterTabs: [],
  activeTypeFilter: null,
  onTypeFilterChange: () => {},
  setTypeFilters: () => {},
};

export { DefaultValue };

const NavTypeFilterContext = createContext<NavTypeFilterContextValue>(DefaultValue);

export function NavTypeFilterProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<NavTypeFilterState>({
    typeFilterTabs: [],
    activeTypeFilter: null,
    onTypeFilterChange: () => {},
  });

  const setTypeFilters = useCallback((next: NavTypeFilterState) => {
    setState((prev) => {
      // Avoid unnecessary updates — bail out if nothing changed
      if (
        prev.activeTypeFilter === next.activeTypeFilter &&
        prev.typeFilterTabs.length === next.typeFilterTabs.length &&
        prev.typeFilterTabs.every((t, i) => t.id === next.typeFilterTabs[i]?.id)
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  // Clear type filters on unmount (e.g. when switching modules)
  useEffect(() => {
    return () => setState({ typeFilterTabs: [], activeTypeFilter: null, onTypeFilterChange: () => {} });
  }, []);

  // Memoize the context value to prevent unnecessary consumer re-renders.
  // Without this, `{ ...state, setTypeFilters }` creates a new object on
  // every provider render, causing all consumers to re-render even when
  // state hasn't changed.
  const value = useMemo<NavTypeFilterContextValue>(
    () => ({ ...state, setTypeFilters }),
    [state, setTypeFilters],
  );

  return (
    <NavTypeFilterContext.Provider value={value}>
      {children}
    </NavTypeFilterContext.Provider>
  );
}

export function useSetNavTypeFilters() {
  const ctx = useContext(NavTypeFilterContext);
  return ctx.setTypeFilters;
}

export function useNavTypeFilters(): NavTypeFilterState {
  const ctx = useContext(NavTypeFilterContext);
  return {
    typeFilterTabs: ctx.typeFilterTabs,
    activeTypeFilter: ctx.activeTypeFilter,
    onTypeFilterChange: ctx.onTypeFilterChange,
  };
}
